import { useMemo, useState } from "react";
import { Loader2, Play, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  useQuestions,
  useQuestionAlternatives,
  useQuestionCategories,
  useEvaluateHelfoEngine,
  useHelfoBiometricMap,
  useHelfoMedicationKeywords,
  type EngineAminoResponse,
  type EvaluateResult,
} from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/cn";

/**
 * Doctor-facing engine test page.
 *
 * Renders every survey question as a form control, keeps responses in local
 * React state, posts to /admin/helfo-engine/evaluate, and renders the
 * EvaluateResult (active clusters, requisition text, lint/dedup, both
 * panels). No persistence — refresh wipes everything.
 *
 * Form value shape:
 *   - single_select  → string (alternative id) | undefined
 *   - multi_select   → string[] (alternative ids)
 *   - numeric        → string (kept as string for free-form editing; coerced
 *                      to number on submit)
 *   - text (other)   → string (free-text — not currently used as engine input)
 */

type FormValue = string | string[] | undefined;
type FormState = Record<string, FormValue>;

// Mirror backend/src/modules/helfo-engine/question-ids.ts — single source
// of truth on the backend side. Duplicated here because the backoffice is
// a separate package; keep in sync if the production UUID ever changes.
const BP_QID = "43aa8eef-6b23-45b0-97c3-b69b6b327fd7";
const MEDICATION_QID = "7652f5d4-2a57-44bc-bbbc-640789c586a9";

/**
 * What kind of input a question takes. Drives both the render branch in
 * QuestionField and the submit branch in handleRun, so the two stay in
 * lockstep — switching on this avoids string-shape-sniffing (which broke
 * for alternative UUIDs that start with a digit).
 */
type QuestionKind =
  | "blood_pressure" // BP_QID — split "sys;dia" → engine overrides
  | "numeric" // isNumber=true, no alts — free-form number input
  | "multi_select" // multipleSelect=true with alts — checkboxes
  | "single_select" // !multipleSelect with alts — dropdown
  | "free_text"; // no alts and not numeric — textarea (engine ignores)

type QuestionLike = {
  id: string;
  isNumber?: boolean | null;
  multipleSelect?: boolean | null;
};

function questionKind(q: QuestionLike, hasAlternatives: boolean): QuestionKind {
  if (q.id === BP_QID) return "blood_pressure";
  if (q.isNumber && !hasAlternatives) return "numeric";
  if (!hasAlternatives) return "free_text";
  return q.multipleSelect ? "multi_select" : "single_select";
}

export function HelfoEngineTestPage() {
  const { data: categories, isLoading: catsLoading } = useQuestionCategories();
  const { data: questions, isLoading: qLoading } = useQuestions();
  const { data: alternatives, isLoading: aLoading } = useQuestionAlternatives();
  const { data: medKeywords } = useHelfoMedicationKeywords();
  const evaluate = useEvaluateHelfoEngine();

  const [form, setForm] = useState<FormState>({});
  const [age, setAge] = useState<string>("");
  const [sex, setSex] = useState<"" | "male" | "female">("");
  const [result, setResult] = useState<EvaluateResult | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  const altsByQuestion = useMemo(() => {
    const map = new Map<
      string,
      { id: string; text: string; order: number }[]
    >();
    for (const a of alternatives ?? []) {
      const arr = map.get(a.questionId) ?? [];
      arr.push({ id: a.id, text: a.text, order: Number(a.order ?? 0) });
      map.set(a.questionId, arr);
    }
    for (const arr of map.values()) arr.sort((x, y) => x.order - y.order);
    return map;
  }, [alternatives]);

  const sortedCategories = useMemo(
    () =>
      (categories ?? [])
        .slice()
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
    [categories],
  );

  if (catsLoading || qLoading || aLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  const handleRun = async () => {
    const responses: EngineAminoResponse[] = [];
    const overrides: Record<string, unknown> = {};
    const questionsById = new Map((questions ?? []).map((q) => [q.id, q]));

    for (const [questionId, value] of Object.entries(form)) {
      if (value === undefined || value === "") continue;
      const q = questionsById.get(questionId);
      if (!q) continue;
      const kind = questionKind(
        q,
        (altsByQuestion.get(questionId) ?? []).length > 0,
      );

      switch (kind) {
        case "blood_pressure": {
          if (typeof value !== "string") break;
          const [sysStr, diaStr] = value.split(";");
          const sys = Number.parseInt(sysStr ?? "", 10);
          const dia = Number.parseInt(diaStr ?? "", 10);
          if (Number.isFinite(sys)) overrides.systolic_bp = sys;
          if (Number.isFinite(dia)) overrides.diastolic_bp = dia;
          break;
        }
        case "numeric": {
          if (typeof value !== "string") break;
          const n = Number.parseFloat(value);
          if (Number.isFinite(n)) {
            responses.push({ amino_question_id: questionId, numeric_value: n });
          }
          break;
        }
        case "multi_select": {
          if (!Array.isArray(value) || value.length === 0) break;
          responses.push({
            amino_question_id: questionId,
            amino_alternative_ids: value,
          });
          break;
        }
        case "single_select": {
          if (typeof value !== "string") break;
          responses.push({
            amino_question_id: questionId,
            amino_alternative_ids: [value],
          });
          break;
        }
        case "free_text":
          // Engine evaluate endpoint has no text-response field. For the
          // medication free-text question we mirror the production adapter's
          // keyword-scrape locally and inject the matched slugs via overrides;
          // otherwise free-text is ignored (engine doesn't read it).
          if (
            questionId === MEDICATION_QID &&
            typeof value === "string" &&
            value.trim().length > 0 &&
            medKeywords
          ) {
            const lower = value.toLowerCase();
            const matched: string[] = [];
            for (const entry of medKeywords) {
              if (entry.keywords.some((kw) => lower.includes(kw))) {
                matched.push(entry.slug);
              }
            }
            if (matched.length > 0) {
              const existing = Array.isArray(overrides.current_medications)
                ? (overrides.current_medications as string[])
                : [];
              overrides.current_medications = Array.from(
                new Set([...existing, ...matched]),
              );
            }
          }
          break;
      }
    }

    const ageNum = Number.parseInt(age, 10);
    try {
      const res = await evaluate.mutateAsync({
        responses,
        options: {
          age: Number.isFinite(ageNum) ? ageNum : undefined,
          sex_at_birth: sex || undefined,
          overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
        },
      });
      setResult(res);
    } catch (err) {
      toast.error("Engine call failed: " + (err as Error).message);
    }
  };

  const handleReset = () => {
    setForm({});
    setAge("");
    setSex("");
    setResult(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">HELFO-motor</h1>
      </div>

      <Tabs defaultValue="test">
        <TabsList>
          <TabsTrigger value="test">Testside</TabsTrigger>
          <TabsTrigger value="mappings">Mappinger</TabsTrigger>
        </TabsList>
        <TabsContent value="test">
          <p className="text-sm text-admin-text-subtle mb-3">
            Fyll inn fiktive svar, kjør motoren og se hva som utløses.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    Demografi (adapter-overrides)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-admin-text-subtle">
                        Alder
                      </label>
                      <Input
                        type="number"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        placeholder="35"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-admin-text-subtle">
                        Kjønn
                      </label>
                      <select
                        value={sex}
                        onChange={(e) =>
                          setSex(e.target.value as "" | "male" | "female")
                        }
                        className="w-full h-9 rounded-md border border-admin-card-border bg-admin-card px-3 text-sm"
                      >
                        <option value="">Ikke spesifisert</option>
                        <option value="female">Kvinne</option>
                        <option value="male">Mann</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {sortedCategories.map((cat) => {
                const catQuestions = (questions ?? [])
                  .filter((q) => q.categoryId === cat.id)
                  .sort(
                    (a, b) =>
                      Number(a.sequenceNumber ?? 0) -
                      Number(b.sequenceNumber ?? 0),
                  );
                if (catQuestions.length === 0) return null;
                return (
                  <CategoryBlock
                    key={cat.id}
                    name={cat.name}
                    questions={catQuestions}
                    altsByQuestion={altsByQuestion}
                    form={form}
                    onChange={(qid, v) => setForm((f) => ({ ...f, [qid]: v }))}
                  />
                );
              })}
            </div>

            <div className="space-y-4">
              <div className="flex gap-2 sticky top-0 z-10 bg-admin-bg py-2">
                <Button
                  onClick={handleRun}
                  disabled={evaluate.isPending}
                  className="flex-1"
                >
                  {evaluate.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Kjører motor…
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Kjør motor
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleReset}>
                  Nullstill
                </Button>
              </div>

              {result ? (
                <EngineResults
                  result={result}
                  showDebug={showDebug}
                  onToggleDebug={() => setShowDebug((s) => !s)}
                />
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-sm text-admin-text-subtle">
                    Trykk «Kjør motor» for å se output.
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="mappings">
          <MappingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mappings tab — biometric slug→DB row + medication keyword reference tables
// ---------------------------------------------------------------------------

function MappingsTab() {
  const { data: biometricMap, isLoading: bmLoading } = useHelfoBiometricMap();
  const { data: medKeywords, isLoading: mkLoading } =
    useHelfoMedicationKeywords();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Biomarkør-mapping ({biometricMap?.length ?? "?"} slugs)
          </CardTitle>
          <p className="text-xs text-admin-text-subtle">
            Motorens slug → Aminos biometric-rad. Tomme rader er slugs som ennå
            ikke finnes i biometric-katalogen.
          </p>
        </CardHeader>
        <CardContent>
          {bmLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-admin-text-subtle" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Motor-slug</TableHead>
                  <TableHead>Amino-navn</TableHead>
                  <TableHead>Enhet</TableHead>
                  <TableHead>NLK</TableHead>
                  <TableHead>Fürst-ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(biometricMap ?? []).map((entry) => (
                  <TableRow key={entry.slug}>
                    <TableCell className="font-mono text-xs">
                      {entry.slug}
                    </TableCell>
                    <TableCell>
                      {entry.biometric ? (
                        <span>{entry.biometric.name}</span>
                      ) : (
                        <Badge variant="destructive">mangler i katalog</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-admin-text-subtle">
                      {entry.biometric?.unit ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-admin-text-subtle">
                      {entry.biometric?.nlk ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-admin-text-subtle">
                      {entry.biometric?.furstId ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Medisin-keywords ({medKeywords?.length ?? "?"} slugs)
          </CardTitle>
          <p className="text-xs text-admin-text-subtle">
            Free-text «Medisiner» blir skannet for disse ordene. Treff injiseres
            som engine-slug i <code>current_medications</code>.
          </p>
        </CardHeader>
        <CardContent>
          {mkLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-admin-text-subtle" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Motor-slug</TableHead>
                  <TableHead>Keywords</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(medKeywords ?? []).map((entry) => (
                  <TableRow key={entry.slug}>
                    <TableCell className="font-mono text-xs align-top">
                      {entry.slug}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {entry.keywords.map((kw) => (
                          <Badge key={kw} variant="secondary">
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category + question form rendering
// ---------------------------------------------------------------------------

type Question = NonNullable<ReturnType<typeof useQuestions>["data"]>[number];
type Alt = { id: string; text: string; order: number };

function CategoryBlock({
  name,
  questions,
  altsByQuestion,
  form,
  onChange,
}: {
  name: string;
  questions: Question[];
  altsByQuestion: Map<string, Alt[]>;
  form: FormState;
  onChange: (qid: string, v: FormValue) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader
        className="pb-2 cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
      >
        <CardTitle className="text-sm flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          {name}
          <span className="text-admin-text-subtle font-normal">
            ({questions.length})
          </span>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          {questions.map((q) => (
            <QuestionField
              key={q.id}
              question={q}
              alternatives={altsByQuestion.get(q.id) ?? []}
              value={form[q.id]}
              onChange={(v) => onChange(q.id, v)}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

function QuestionField({
  question,
  alternatives,
  value,
  onChange,
}: {
  question: Question;
  alternatives: Alt[];
  value: FormValue;
  onChange: (v: FormValue) => void;
}) {
  const kind = questionKind(question, alternatives.length > 0);
  const Label = (
    <label className="text-xs font-medium text-admin-text-secondary">
      {question.question}
    </label>
  );

  switch (kind) {
    case "blood_pressure": {
      // Render as two number inputs and combine into "sys;dia" — same shape
      // production stores. handleRun splits it back into engine fields.
      const [sys, dia] =
        typeof value === "string" ? value.split(";") : ["", ""];
      const update = (s: string, d: string) => {
        if (!s && !d) onChange(undefined);
        else onChange(`${s};${d}`);
      };
      return (
        <div>
          {Label}
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div>
              <label className="text-[10px] text-admin-text-subtle">
                Systolisk
              </label>
              <Input
                type="number"
                value={sys ?? ""}
                onChange={(e) => update(e.target.value, dia ?? "")}
                placeholder="120"
              />
            </div>
            <div>
              <label className="text-[10px] text-admin-text-subtle">
                Diastolisk
              </label>
              <Input
                type="number"
                value={dia ?? ""}
                onChange={(e) => update(sys ?? "", e.target.value)}
                placeholder="80"
              />
            </div>
          </div>
        </div>
      );
    }
    case "numeric":
      return (
        <div>
          {Label}
          <Input
            type="number"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        </div>
      );
    case "free_text":
      return (
        <div>
          {Label}
          <Textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || undefined)}
            rows={2}
          />
        </div>
      );
    case "multi_select": {
      const selected = (Array.isArray(value) ? value : []) as string[];
      const toggle = (id: string) => {
        if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
        else onChange([...selected, id]);
      };
      return (
        <div>
          {Label}
          <div className="space-y-1 mt-1">
            {alternatives.map((a) => (
              <label
                key={a.id}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(a.id)}
                  onChange={() => toggle(a.id)}
                />
                {a.text}
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "single_select":
      return (
        <div>
          {Label}
          <select
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || undefined)}
            className="mt-1 w-full h-9 rounded-md border border-admin-card-border bg-admin-card px-3 text-sm"
          >
            <option value="">— ikke svart —</option>
            {alternatives.map((a) => (
              <option key={a.id} value={a.id}>
                {a.text}
              </option>
            ))}
          </select>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Engine output rendering
// ---------------------------------------------------------------------------

function EngineResults({
  result,
  showDebug,
  onToggleDebug,
}: {
  result: EvaluateResult;
  showDebug: boolean;
  onToggleDebug: () => void;
}) {
  const helfo = result.helfo;
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">HELFO — aktive clusters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {helfo.active_clusters.length === 0 ? (
            <p className="text-sm text-admin-text-subtle">
              Ingen NOKLUS-cluster utløst.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {helfo.cluster_details.map((c) => (
                <Badge key={c.cluster_id} variant="default">
                  {c.cluster_name}
                </Badge>
              ))}
            </div>
          )}
          {helfo.suppressed_clusters.length > 0 && (
            <div className="pt-2 border-t border-admin-card-border">
              <p className="text-xs text-admin-text-subtle mb-1">Undertrykt:</p>
              <div className="flex flex-wrap gap-1.5">
                {helfo.suppressed_clusters.map((s) => (
                  <Badge key={s.cluster_id} variant="secondary">
                    {s.cluster_name} ({s.status})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">HELFO — biomarkører</CardTitle>
        </CardHeader>
        <CardContent>
          {helfo.biomarkers.length === 0 ? (
            <p className="text-sm text-admin-text-subtle">Ingen.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {helfo.biomarkers.map((b) => (
                <Badge key={b} variant="info">
                  {b}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Kliniske opplysninger (lint-validert)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <pre className="whitespace-pre-wrap text-sm text-admin-text-primary bg-admin-bg p-2 rounded border border-admin-card-border">
            {helfo.requisition_text}
          </pre>
          {helfo.clinicalInfoViolations.length > 0 && (
            <div className="space-y-1">
              {helfo.clinicalInfoViolations.map((v, i) => (
                <Badge key={i} variant="destructive" className="block w-fit">
                  {v.type === "forbidden_phrase"
                    ? `Forbudt frase: ${v.phrase}`
                    : v.message}
                </Badge>
              ))}
            </div>
          )}
          {helfo.dedup_warnings.length > 0 && (
            <div className="space-y-1">
              {helfo.dedup_warnings.map((w, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className={cn("block w-fit border-admin-warn-500 text-admin-warn-400")}
                >
                  Dedup: {w.reason}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Privat-panel (kun privat)</CardTitle>
        </CardHeader>
        <CardContent>
          {result.comparison.only_in_privat.length === 0 ? (
            <p className="text-sm text-admin-text-subtle">Ingen.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {result.comparison.only_in_privat.map((b) => (
                <Badge key={b} variant="secondary">
                  {b}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          className="pb-2 cursor-pointer select-none"
          onClick={onToggleDebug}
        >
          <CardTitle className="text-sm flex items-center gap-1.5">
            {showDebug ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Debug — oversatt engine-survey
          </CardTitle>
        </CardHeader>
        {showDebug && (
          <CardContent>
            <pre className="text-xs text-admin-text-subtle bg-admin-bg p-2 rounded border border-admin-card-border overflow-x-auto">
              {JSON.stringify(result.survey, null, 2)}
            </pre>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
